import { Box, Container, Divider, Grid, Skeleton, Stack, Typography, styled } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { AssetsPane } from "Components/Analyses/Assets/AssetsPane";
import { AssetsAndContractsPane } from "Components/Analyses/AssetsAndContracts/AssetsAndContractsPane";
import { BudgetPane } from "Components/Analyses/Budget/BudgetPane";
import { AnalysisDetailContext } from "Components/Analyses/Detail/AnalysisDetailContext";
import { AnalysisMoreActions } from "Components/Analyses/Detail/AnalysisMoreActions";
import { AnalysisStatusBar } from "Components/Analyses/Detail/AnalysisStatusBar";
import { AnalysisStatusSelect } from "Components/Analyses/Detail/AnalysisStatusSelect";
import { AnalysisWriteForbiddenModal } from "Components/Analyses/Detail/AnalysisWriteForbiddenModal";
import { InternalNoteModal } from "Components/Analyses/Detail/InternalNoteModal";
import { InvestmentCaseGate } from "Components/Analyses/Detail/InvestmentCaseGate";
import { AnalysisDetailSection, MultilevelTabMenu } from "Components/Analyses/Detail/MultilevelTabMenu";
import { VersionSelect } from "Components/Analyses/Detail/VersionSelect";
import { useAnalysisAccess } from "Components/Analyses/Detail/useAnalysisAccess";
import { useAnalysisHeaderPolling } from "Components/Analyses/Detail/useAnalysisHeaderPolling";
import { FamilyPane } from "Components/Analyses/Family/FamilyPane";
import { GoalsPane } from "Components/Analyses/Goals/GoalManagement/GoalsPane";
import { GoalsFulfillmentPane } from "Components/Analyses/GoalsFulfillment/GoalsFulfillmentPane";
import { InsurancePane } from "Components/Analyses/Insurance/InsurancePane";
import { ModelingPane } from "Components/Analyses/Modeling/ModelingPane";
import { QuestionnairePane } from "Components/Analyses/Questionnaire/QuestionnairePane";
import { ResultDocumentsPane } from "Components/Analyses/Result/ResultDocumentsPane";
import { BasicInfoBox } from "Components/Analyses/_Shared/BasicInfoBox";
import { HStack } from "Components/_Shared/Core/Stack";
import { ReactComponent as LeftIcon } from "Components/_Shared/Icons/Assets/left.svg";
import { EditableTitle } from "Components/_Shared/Inputs/EditableTitle";
import { AuthenticatedLayout } from "Components/_Shared/Layouts/AuthenticatedLayout";
import { StyledBackground } from "Components/_Shared/Layouts/StyledBackground";
import { UnstyledLink } from "Components/_Shared/Routing/UnstyledLink";
import {
  getGetAnalysisQueryKey,
  useCloneAnalysis,
  useGetAnalysis,
  usePatchAnalysis,
  useSetAnalysisState,
} from "Infrastructure/ApiZfp/AnalyzerApi";
import { AnalysisDetailState } from "Infrastructure/ApiZfp/Schema";
import { useDateFnsLocale } from "Infrastructure/Hooks/DateFns/useDateFnsLocale";
import { useAnalysisDetailSection } from "Infrastructure/Hooks/useAnalysisDetailSection";
import { FeatureFlag, useFeatureFlag } from "Infrastructure/Hooks/useFeatureFlag";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { AppRouting } from "Infrastructure/Utils/UrlUtils";
import { format } from "date-fns";
import type { FC } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

export const AnalysisDetail: FC = () => {
  const { t } = useResource();
  const locale = useDateFnsLocale();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { analysisId, versionId } = useParams() as {
    analysisId: string;
    versionId: string;
  };
  const {
    data: analysisHeader,
    isSuccess,
    isLoading,
  } = useGetAnalysis(analysisId, versionId, {
    query: {
      // the advisor number comes in square brackets ("Name [123456]"), the UI shows it in round ones
      select: (analysis) => ({ ...analysis, agentName: analysis.agentName.replace(/\[([^\]]*)\]$/, "($1)") }),
    },
  });
  const { section, setSection } = useAnalysisDetailSection(AnalysisDetailSection.Family);
  const isAnalysisSharingEnabled = useFeatureFlag(FeatureFlag.AnalysisSharing);
  useAnalysisHeaderPolling(analysisId, versionId, isAnalysisSharingEnabled);

  const { mutate: setAnalysisState, isPending: isSettingState } = useSetAnalysisState({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getGetAnalysisQueryKey(analysisId, versionId) });
      },
    },
  });

  const { mutate: patchAnalysis, mutateAsync: patchAnalysisAsync } = usePatchAnalysis({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getGetAnalysisQueryKey(analysisId, versionId) });
      },
    },
  });

  const { mutate: cloneAnalysis, isPending: isCloning } = useCloneAnalysis({
    mutation: {
      onSuccess: (result) => {
        toast.success(t(Resources.AnalysisDetail.Toast.NewVersionCreated, { version: result.version }));
        navigate(`${AppRouting.AnalysisDetail.path(result.analysisId, result.versionId)}${location.search}`);
      },
      onError: () => {
        toast.error(t(Resources.AnalysisDetail.Toast.ActionError));
      },
    },
  });

  const isMutatingState = isCloning || isSettingState;

  const handleCreateNewVersion = () => {
    if (isMutatingState) return;

    cloneAnalysis({ analysisId, versionId });
  };

  const handleStateChange = (newState: AnalysisDetailState) => {
    if (isMutatingState) return;

    if (newState === AnalysisDetailState.in_progress) {
      handleCreateNewVersion();
      return;
    }

    setAnalysisState({
      analysisId,
      versionId,
      data: {
        state: newState,
      },
    });
  };

  const selectedVersion = analysisHeader?.versions.find((version) => version.versionId === versionId);
  const latestVersion = analysisHeader?.versions.find((version) => !version.readOnly);
  const access = useAnalysisAccess(analysisHeader);

  return (
    <AnalysisDetailContext.Provider
      value={{ analysisId, versionId, readOnly: !access.isEditable, canAddFamilyMember: access.canAddFamilyMember }}
    >
      <AuthenticatedLayout title={analysisHeader?.name || ""} appBarVariant="dark" container={false}>
        <Stack width="100%" spacing={Spacing.XL}>
          <Box>
            <StyledBackground>
              <Box pt={Spacing.M}>
                <Container>
                  <Stack spacing={Spacing.S} pt={Spacing.L}>
                    <UnstyledLink to={AppRouting.Analyses.path()}>
                      <Box display="flex" alignItems="center">
                        <LeftIcon />
                        <Typography color="secondary" variant="body2" fontWeight={600} ml={2}>
                          {t(Resources.AnalysisDetail.AnalysisOverview)}
                        </Typography>
                      </Box>
                    </UnstyledLink>
                    {isSuccess && (
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <EditableTitle
                          value={analysisHeader.name}
                          onChange={(value) => {
                            patchAnalysis({
                              analysisId,
                              versionId,
                              data: { name: value },
                            });
                          }}
                          readOnly={!access.isEditable}
                        />
                        <Box display="flex" alignItems="center" justifyContent="center" sx={{ minWidth: "20%" }}>
                          <VersionSelect versions={analysisHeader.versions} />
                          <Divider orientation="vertical" sx={{ height: 24, mx: Spacing.M, mb: Spacing.S }} />
                          <HStack pt={Spacing.XS}>
                            <InternalNoteModal
                              content={analysisHeader.note ?? ""}
                              onSave={async (content) => {
                                await patchAnalysisAsync({
                                  analysisId,
                                  versionId,
                                  data: { note: content },
                                });
                                toast.success(t(Resources.AnalysisDetail.Toast.NoteSaved));
                              }}
                            />
                            <AnalysisMoreActions
                              analysisName={analysisHeader.name}
                              displayedVersion={selectedVersion}
                              latestVersion={latestVersion}
                              access={access}
                            />
                          </HStack>
                        </Box>
                      </Box>
                    )}
                  </Stack>

                  <Grid container my={Spacing.XXL} spacing={16} display="flex" alignItems="center">
                    {isLoading && (
                      <>
                        <Skeleton variant="rounded" />
                        <Skeleton variant="rounded" />
                        <Skeleton variant="rounded" />
                        <Skeleton variant="rounded" />
                      </>
                    )}
                    {isSuccess && (
                      <>
                        <AnalysisStatusSelect
                          state={analysisHeader.state}
                          onStateChange={handleStateChange}
                          disabled={isMutatingState}
                          readOnly={!access.canChangeState}
                        />
                        <BasicInfoBox title={t(Resources.AnalysisDetail.Client)} value={analysisHeader.clientName} />
                        <BasicInfoBox
                          title={t(Resources.AnalysisDetail.DateCreated)}
                          value={format(new Date(analysisHeader.createdAt), "d. MMMM yyyy", {
                            locale,
                          })}
                        />
                        <BasicInfoBox title={t(Resources.AnalysisDetail.Agent)} value={analysisHeader.agentName} />
                      </>
                    )}
                  </Grid>
                </Container>
              </Box>
            </StyledBackground>
            {isSuccess && (
              <AnalysisStatusBar
                analysis={analysisHeader}
                access={access}
                isCreatingNewVersion={isCloning}
                onCreateNewVersion={handleCreateNewVersion}
              />
            )}
          </Box>
          <Box>
            <Container>
              <Stack spacing={Spacing.XXL}>
                <MultilevelTabMenu
                  value={section}
                  onChange={(newSection) => {
                    setSection(newSection);
                  }}
                />
                <InvestmentCaseGate>
                  {section === AnalysisDetailSection.Family && <FamilyPane />}
                  {section === AnalysisDetailSection.Goals && <GoalsPane />}
                  {section === AnalysisDetailSection.Budget && <BudgetPane analysisId={analysisId} versionId={versionId} />}
                  {section === AnalysisDetailSection.Assets && <AssetsPane />}
                  {section === AnalysisDetailSection.GoalFulfillment && <GoalsFulfillmentPane />}
                  {section === AnalysisDetailSection.Insurance && <InsurancePane analysisId={analysisId} versionId={versionId} />}
                  {section === AnalysisDetailSection.Modeling && <ModelingPane />}
                  {section === AnalysisDetailSection.AssetsAndContracts && <AssetsAndContractsPane />}
                  {section === AnalysisDetailSection.ResultDocuments && <ResultDocumentsPane />}
                  {section === AnalysisDetailSection.InvestmentQuestionnaire && <QuestionnairePane />}
                </InvestmentCaseGate>
              </Stack>
            </Container>
          </Box>
        </Stack>
        <AnalysisWriteForbiddenModal />
      </AuthenticatedLayout>
    </AnalysisDetailContext.Provider>
  );
};
